/**
 * Variant Availability Filter — HIG-35
 * ------------------------------------------------------------------
 * Marks option-2 (and option-3) values as "non-existing" when the
 * currently-selected option-1 has no variant that pairs with them.
 *
 * Example: Whey Protein has Geschmack (option-1) + Menge (option-2).
 * Most Mengen only exist for a subset of Geschmäcker. Selecting
 * "Extreme Milk Chocolate" should grey-out the Mengen that don't
 * have a matching variant.
 *
 * Directionality:
 *   Only option-2 / option-3 are filtered, NEVER option-1. Filtering
 *   option-1 by option-2 would hide colors when a single size limits
 *   the visible palette — that's the wrong UX (the user might want
 *   to re-pick size).
 *
 * Stacks with Shopify's `available` (OOS) flag:
 *   This module adds the class `is-non-existing` to inputs whose
 *   combination doesn't exist as a Shopify variant.
 *   product.css / swatches-styles.css render `.is-non-existing` with
 *   the same strike-through + opacity treatment as `.is-unavailable`
 *   (true sold-out). Two separate classes so the variant-picker.js
 *   OOS logic and this module don't fight over a single class.
 *
 * Data source:
 *   <variant-picker> emits a `<script type="application/json"
 *   data-all-variants>` with the full variant matrix (id, option1,
 *   option2, option3, available). Tiny payload, no extra fetch.
 *
 * Triggers:
 *   - Initial render (DOMContentLoaded + every <variant-picker>
 *     encountered).
 *   - Every change event on the picker (a variant-change fires the
 *     existing on:variant:change too, but we listen to the lower-
 *     level 'change' to also catch in-progress option-1 swaps
 *     before the variant-fetch resolves).
 *
 * Idempotent: refilter() is safe to run any number of times.
 */
(function () {
  // Guard: this file can be loaded both by variant-picker.liquid (PDP) and by
  // quick-add-drawer.liquid (global, so the observer also runs on pages whose
  // drawer opens a picker — collection/home/slider). Run the IIFE only once.
  if (window.__hpVariantAvailabilityInit) return;
  window.__hpVariantAvailabilityInit = true;

  function processPicker(picker) {
    const dataEl = picker.querySelector('script[type="application/json"][data-all-variants]');
    if (!dataEl) return;
    let variants;
    try { variants = JSON.parse(dataEl.textContent); } catch { return; }
    if (!Array.isArray(variants) || variants.length === 0) return;

    const selectors = picker.querySelectorAll('.option-selector:not(.option-selector--product-group)');
    if (selectors.length < 2) return; // single-option products: nothing to filter

    function selectedValueFor(selector) {
      const radio = selector.querySelector('.js-option:checked');
      if (radio) return radio.value;
      const dropdown = selector.querySelector('.custom-select__option[aria-selected="true"]');
      if (dropdown && dropdown.dataset.value !== '') return dropdown.dataset.value;
      return null;
    }

    // Tobias 2026-06-04: contextual sold-out BELL, BIDIRECTIONAL. For EVERY option value,
    // if a variant with that value + the currently-selected OTHER options EXISTS but is
    // sold out (none available), mark it `is-unavailable` (→ bell, clickable for STOQ
    // notify). This is the gap the server-side is-unavailable (line 307 of
    // variant-picker.liquid: only values sold out across ALL combos) and the one-way
    // is-non-existing cascade left open. Example (Langhantel-Set): pick the Olympic
    // plates (red 25 kg sold out) → the heaviest "Complete" set shows a bell, even though
    // Complete is buyable with the OTHER (Schwarz) plates.
    //   Bidirectional is OK here (unlike is-non-existing, which HIG-35 kept one-way to
    //   avoid HIDING colours): a bell only INFORMS + stays clickable, it never hides a
    //   value, so "Complete is sold out with these plates" is helpful, not a dead end.
    function markContextualSoldOut() {
      const sel = [];
      selectors.forEach((s) => sel.push(selectedValueFor(s)));

      function isContextSoldOut(idx, val) {
        let exists = false;
        for (const v of variants) {
          const opts = [v.option1, v.option2, v.option3];
          if (opts[idx] !== val) continue;
          let matchesOthers = true;
          for (let j = 0; j < selectors.length; j++) {
            if (j === idx) continue;
            if (sel[j] != null && opts[j] !== sel[j]) { matchesOthers = false; break; }
          }
          if (!matchesOthers) continue;
          exists = true;
          if (v.available) return false; // an available combo exists → not sold out
        }
        return exists; // exists but no available combo found → sold out in context
      }

      selectors.forEach((selector, idx) => {
        const mark = (el, value) => {
          // A non-existing combo (handled separately) gets no bell.
          if (el.classList.contains('is-non-existing')) { el.classList.remove('is-unavailable'); return; }
          el.classList.toggle('is-unavailable', isContextSoldOut(idx, value));
        };
        selector.querySelectorAll('input.js-option').forEach((inp) => mark(inp, inp.value));
        selector.querySelectorAll('.custom-select__option').forEach((opt) => {
          if (opt.dataset.value !== '') mark(opt, opt.dataset.value);
        });
      });
    }

    function refilter() {
      const v1 = selectedValueFor(selectors[0]);
      if (v1 == null) return;

      // Pass 1: is-non-existing — one-way cascade (option1→2→3), HIG-35 (downstream only,
      // so we never HIDE an upstream option).
      const validBySel = [];
      for (let i = 1; i < selectors.length; i++) {
        const sel = selectors[i];
        const valid = new Set();
        const v2 = i >= 2 ? selectedValueFor(selectors[1]) : null;
        for (const variant of variants) {
          if (variant.option1 !== v1) continue;
          if (i >= 2 && variant.option2 !== v2) continue;
          const k = i === 1 ? variant.option2 : variant.option3;
          if (k != null) valid.add(k);
        }
        validBySel[i] = valid;
        sel.querySelectorAll('input.js-option').forEach((inp) => {
          inp.classList.toggle('is-non-existing', !valid.has(inp.value));
        });
        sel.querySelectorAll('.custom-select__option').forEach((opt) => {
          if (opt.dataset.value === '') return;
          opt.classList.toggle('is-non-existing', !valid.has(opt.dataset.value));
        });
      }

      // Pass 2: contextual sold-out bells (is-unavailable), bidirectional.
      markContextualSoldOut();

      // Pass 3: auto-correct (Tobias 2026-06-01) — if the value still selected in a
      // downstream option no longer pairs with the upstream selection (combination
      // doesn't exist), jump to the first EXISTING + IN-STOCK value. A still-valid value
      // is kept (size stays when you click through colours). Prevents ?variant=undefined
      // dead-ends. (Full "pick best available variant" = HIG-60.)
      for (let i = 1; i < selectors.length; i++) {
        const v2 = i >= 2 ? selectedValueFor(selectors[1]) : null;
        if (autoCorrectSelector(selectors[i], i, v1, v2, validBySel[i])) return;
      }
    }

    function autoCorrectSelector(sel, i, v1, v2, valid) {
      if (picker.__hpAutoSelect) return false;
      const radio = sel.querySelector('input.js-option:checked');
      const dropdown = sel.querySelector('.custom-select__option[aria-selected="true"]');
      const currentVal = radio
        ? radio.value
        : (dropdown && dropdown.dataset.value !== '' ? dropdown.dataset.value : null);
      if (currentVal == null || valid.has(currentVal)) return false;

      const isAvailable = (val) => variants.some((v) =>
        v.option1 === v1
        && (i === 1 ? v.option2 === val : (v.option2 === v2 && v.option3 === val))
        && v.available);

      const candidates = [];
      if (radio) {
        sel.querySelectorAll('input.js-option').forEach((inp) => {
          if (valid.has(inp.value)) candidates.push(inp);
        });
      } else {
        sel.querySelectorAll('.custom-select__option').forEach((opt) => {
          if (opt.dataset.value !== '' && valid.has(opt.dataset.value)) candidates.push(opt);
        });
      }
      if (!candidates.length) return false;

      // first in-stock candidate (document order = first/smallest); else first valid
      const pick = candidates.find((el) => isAvailable(radio ? el.value : el.dataset.value))
        || candidates[0];

      picker.__hpAutoSelect = true;
      setTimeout(() => {
        try {
          if (radio) {
            const lbl = sel.querySelector(`label[for="${pick.id}"]`);
            (lbl || pick).click();
          } else {
            const cs = pick.closest('custom-select');
            if (cs && typeof cs.selectOption === 'function') cs.selectOption(pick);
            else pick.click();
          }
        } finally {
          setTimeout(() => { picker.__hpAutoSelect = false; }, 60);
        }
      }, 0);
      return true; // re-runs refilter via the triggered change; handle one option/pass
    }

    picker.addEventListener('change', () => {
      // Defer one tick so radio's :checked state has updated.
      setTimeout(refilter, 0);
    });
    refilter();
  }

  // Process a picker at most once (its element identity). The quick-add /
  // choose-options drawer injects a fresh <variant-picker> when it opens AND
  // variant-picker.js recreates the element on each variant change, so the
  // observer below sees a new element each time — each gets processed once.
  function processPickerOnce(picker) {
    if (picker.dataset.hpAvailabilityProcessed) return;
    picker.dataset.hpAvailabilityProcessed = '1';
    processPicker(picker);
  }

  function init() {
    document.querySelectorAll('variant-picker').forEach(processPickerOnce);
  }

  // Catch variant-pickers added after load — specifically the choose-options
  // drawer (HIG-43): without this, non-existing option combinations aren't
  // greyed out in the drawer the way they are on the PDP.
  function observe() {
    if (!('MutationObserver' in window)) return;
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('variant-picker')) processPickerOnce(node);
          if (node.querySelectorAll) node.querySelectorAll('variant-picker').forEach(processPickerOnce);
        });
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { init(); observe(); });
  } else {
    init();
    observe();
  }
})();
